import { useState } from "react";

import {
  DecisionFilterButton,
  DecisionFilterChevron,
  DecisionFilterDropdownCard,
  DecisionFilterDropdownMotion,
  DecisionFilterMenuAnchor,
  DecisionFilterOptionButton,
} from "@Components/game/session-results/video/styled-components";
import HapticsButtonWrapper from "@Components/generic/haptics-button-wrapper";
import Icon from "@Components/generic/icon";
import { faChevronDown, faFilter } from "@fortawesome/pro-solid-svg-icons";
import { type ReplayPassType } from "@hooks/common/session/replay-decision-utils";
import { SessionFeedbackModel } from "@hooks/game/activity/session/use-session-feedback-model";
import useI18n from "@i18n";
import { ClickAwayListener, Stack, Typography } from "@mui/material";
import { AnimatePresence, Transition } from "framer-motion";

interface IDecisionFilterMenu {
  feedbackModel: SessionFeedbackModel;
}

const DROPDOWN_ANIMATION = {
  duration: 0.18,
  ease: "easeOut",
} satisfies Transition;

const DecisionFilterMenu = ({ feedbackModel }: IDecisionFilterMenu) => {
  const { i18n } = useI18n();
  const [isOpen, setIsOpen] = useState(false);
  const { replay } = feedbackModel;
  const decisionFilter = replay.decisionFilter;

  if (!decisionFilter.isEnabled || replay.areDecisionsLoading || replay.allDecisions.length <= 0) {
    return null;
  }

  const activeOption = decisionFilter.options.find(option => option.value === decisionFilter.activeFilter) ?? decisionFilter.options[0];

  const handleSelectFilter = (filterValue: ReplayPassType | null) => {
    decisionFilter.setActiveFilter(filterValue);
    window.setTimeout(() => setIsOpen(false), 120);
  };

  return (
    <ClickAwayListener onClickAway={() => setIsOpen(false)}>
      <DecisionFilterMenuAnchor>
        <HapticsButtonWrapper>
          <DecisionFilterButton onClick={() => setIsOpen(prev => !prev)} aria-label={i18n("session-results.decision-filter.label")}>
            <Icon icon={faFilter} size="sm" color="white" />
            <Typography variant="textBase600" color="inherit" noWrap>
              {activeOption ? i18n(activeOption.labelKey) : i18n("session-results.decision-filter.label")}
            </Typography>
            <Typography variant="textBase600" color="inherit">
              {activeOption?.count ?? replay.decisions.length}
            </Typography>
            <DecisionFilterChevron animate={{ rotate: isOpen ? 180 : 0 }} transition={DROPDOWN_ANIMATION}>
              <Icon icon={faChevronDown} size="xs" color="white" />
            </DecisionFilterChevron>
          </DecisionFilterButton>
        </HapticsButtonWrapper>

        <AnimatePresence>
          {isOpen && (
            <DecisionFilterDropdownMotion
              initial={{ opacity: 0, y: -8, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -6, scale: 0.98 }}
              transition={DROPDOWN_ANIMATION}
            >
              <DecisionFilterDropdownCard elevation={0}>
                <Stack spacing={0.75}>
                  {decisionFilter.options.map((option, index) => {
                    const isActive = option.value === decisionFilter.activeFilter;
                    const isDisabled = option.count <= 0 && option.value !== null;

                    return (
                      <DecisionFilterOptionButton
                        key={option.value ?? "all"}
                        type="button"
                        initial={{ opacity: 0, x: 8 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ ...DROPDOWN_ANIMATION, delay: index * 0.025 }}
                        whileHover={isDisabled ? undefined : { scale: 1.02 }}
                        whileTap={isDisabled ? undefined : { scale: 0.98 }}
                        disabled={isDisabled}
                        isActive={isActive}
                        isDisabled={isDisabled}
                        onClick={() => handleSelectFilter(option.value)}
                      >
                        <Typography variant="textBase600" color="inherit" noWrap>
                          {i18n(option.labelKey)}
                        </Typography>
                        <Typography variant="textBase600" color="inherit">
                          {option.count}
                        </Typography>
                      </DecisionFilterOptionButton>
                    );
                  })}
                </Stack>
              </DecisionFilterDropdownCard>
            </DecisionFilterDropdownMotion>
          )}
        </AnimatePresence>
      </DecisionFilterMenuAnchor>
    </ClickAwayListener>
  );
};

export default DecisionFilterMenu;
